import React from 'react';
import { trendPaths, trendSummary } from '../studentHomeRules';

/**
 * Scores over a period. It needs no measuring: the line is a stretched 0–100 box
 * (`non-scaling-stroke` keeps its weight), the dots are positioned in percent so
 * they stay round, and each day's number is printed under it, so a phone needs no
 * hover to read one. A day with no score has no dot; the line runs past it. `tall`
 * gives it twice the height, for a page where the trend is the point.
 */
const TrendChart = ({ points, tall = false }) => {
  const { dots, line, area } = trendPaths(points);
  const columns = { gridTemplateColumns: `repeat(${points.length}, minmax(0, 1fr))` };

  return (
    <div role="img" aria-label={trendSummary(points)}>
      <div className={tall ? 'tw-relative tw-h-48' : 'tw-relative tw-h-24'} aria-hidden="true">
        <span className="tw-absolute tw-inset-x-0 tw-top-1/2 tw-border-0 tw-border-t tw-border-dashed tw-border-line" />
        <span className="tw-absolute tw-inset-x-0 tw-bottom-0 tw-border-0 tw-border-t tw-border-solid tw-border-line" />
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="tw-absolute tw-inset-0 tw-h-full tw-w-full tw-overflow-visible">
          {area && <path d={area} className="tw-fill-primary/10" />}
          {line && (
            <path
              d={line}
              fill="none"
              strokeWidth="2.5"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
              className="tw-stroke-primary"
            />
          )}
        </svg>
        {dots.map((dot) => (
          <span
            key={dot.x}
            style={{ left: `${dot.x}%`, top: `${dot.y}%` }}
            className="tw-absolute tw-h-2 tw-w-2 tw--translate-x-1/2 tw--translate-y-1/2 tw-rounded-full tw-bg-primary tw-ring-2 tw-ring-surface-raised"
          />
        ))}
      </div>
      <div className="tw-mt-1.5 tw-grid tw-text-center" style={columns} aria-hidden="true">
        {points.map((point, i) => (
          // Two points can share a label (two interviews on one day); their order is fixed.
          // eslint-disable-next-line react/no-array-index-key
          <div key={`${point.label}-${i}`} className="tw-min-w-0">
            <div className="tw-text-[0.6875rem] tw-font-semibold tw-tabular-nums tw-text-content">
              {point.value === null ? '–' : point.value}
            </div>
            <div className="tw-truncate tw-text-[0.625rem] tw-text-content-subtle">{point.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TrendChart;
